import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";

const LEAD_STEPS = ["name", "phone", "email", "serviceRequired", "message"];

const LEAD_PROMPTS = {
  name: "Great! Let's get your details. What is your name?",
  phone: "Thanks! What is your phone number?",
  email: "Your email address?",
  serviceRequired:
    "Which service do you need? (e.g. Logo Design, Branding, Social Media Posts)",
  message: "Please share a short message about your project:",
};

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function useChatbot() {
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [sending, setSending] = useState(false);
  const [leadMode, setLeadMode] = useState(false);
  const [leadStepIndex, setLeadStepIndex] = useState(0);
  const [leadDraft, setLeadDraft] = useState({});
  const [submittingLead, setSubmittingLead] = useState(false);
  const initialized = useRef(false);

  const addBotMessage = useCallback((text, extra = {}) => {
    setMessages((prev) => [
      ...prev,
      { id: uid(), role: "bot", text, quickReplies: extra.quickReplies || [] },
    ]);
  }, []);

  const addUserMessage = useCallback((text) => {
    setMessages((prev) => [...prev, { id: uid(), role: "user", text }]);
  }, []);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const { data } = await api.get("/api/chatbot/settings");
      setSettings(data);
      return data;
    } catch (e) {
      setSettingsError(e?.response?.data?.message || e?.message || "Could not load chatbot");
      return null;
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (initialized.current || settingsLoading) return;
    if (settings?.welcomeMessage) {
      initialized.current = true;
      addBotMessage(settings.welcomeMessage, {
        quickReplies: ["Our Services", "Pricing", "Contact Us", "Get a Quote"],
      });
    }
  }, [settings, settingsLoading, addBotMessage]);

  const sendToApi = useCallback(
    async (text) => {
      setTyping(true);
      try {
        const { data } = await api.post("/api/chatbot/message", { message: text });
        addBotMessage(data.reply, { quickReplies: data.quickReplies });
        if (data.suggestLeadCapture) {
          setTimeout(() => {
            addBotMessage('Tap "Get a Quote" below to share your project details with our team.', {
              quickReplies: ["Get a Quote"],
            });
          }, 400);
        }
      } catch (e) {
        addBotMessage(
          e?.response?.data?.message ||
            "Sorry, I couldn't process that right now. Please try again or use our contact form."
        );
      } finally {
        setTyping(false);
      }
    },
    [addBotMessage]
  );

  const startLeadCapture = useCallback(() => {
    setLeadMode(true);
    setLeadStepIndex(0);
    setLeadDraft({});
    addBotMessage(LEAD_PROMPTS.name);
  }, [addBotMessage]);

  const submitLead = useCallback(
    async (draft) => {
      setSubmittingLead(true);
      try {
        const { data } = await api.post("/api/chatbot/leads", draft);
        addBotMessage(data.message || "Thank you! Our team will contact you shortly.", {
          quickReplies: ["Our Services", "Contact Us"],
        });
        setLeadMode(false);
        setLeadStepIndex(0);
        setLeadDraft({});
      } catch (e) {
        addBotMessage(
          e?.response?.data?.message || "Could not submit your details. Please try again."
        );
      } finally {
        setSubmittingLead(false);
      }
    },
    [addBotMessage]
  );

  const handleLeadInput = useCallback(
    async (text) => {
      const step = LEAD_STEPS[leadStepIndex];
      const nextDraft = { ...leadDraft, [step]: text };
      setLeadDraft(nextDraft);

      const nextIndex = leadStepIndex + 1;
      if (nextIndex < LEAD_STEPS.length) {
        setLeadStepIndex(nextIndex);
        addBotMessage(LEAD_PROMPTS[LEAD_STEPS[nextIndex]]);
        return;
      }

      await submitLead(nextDraft);
    },
    [leadDraft, leadStepIndex, addBotMessage, submitLead]
  );

  const sendMessage = useCallback(
    async (rawText) => {
      const text = String(rawText || "").trim();
      if (!text || sending || submittingLead) return;

      addUserMessage(text);
      setInput("");

      if (text.toLowerCase() === "get a quote" || text.toLowerCase() === "get quote") {
        startLeadCapture();
        return;
      }

      if (leadMode) {
        await handleLeadInput(text);
        return;
      }

      setSending(true);
      await sendToApi(text);
      setSending(false);
    },
    [
      sending,
      submittingLead,
      leadMode,
      addUserMessage,
      startLeadCapture,
      handleLeadInput,
      sendToApi,
    ]
  );

  const handleQuickReply = useCallback(
    (label) => {
      sendMessage(label);
    },
    [sendMessage]
  );

  const resetChat = useCallback(() => {
    initialized.current = false;
    setMessages([]);
    setLeadMode(false);
    setLeadStepIndex(0);
    setLeadDraft({});
    if (settings?.welcomeMessage) {
      initialized.current = true;
      addBotMessage(settings.welcomeMessage, {
        quickReplies: ["Our Services", "Pricing", "Contact Us", "Get a Quote"],
      });
    }
  }, [settings, addBotMessage]);

  return {
    settings,
    settingsLoading,
    settingsError,
    messages,
    input,
    setInput,
    typing,
    sending: sending || submittingLead,
    sendMessage,
    handleQuickReply,
    resetChat,
    loadSettings,
    leadMode,
  };
}
